// 画像をアップロードして fileKey を親に返す。再選択や削除も可能。
// Phase A の /api/admin/uploads を使用。許容は png/jpeg/webp/gif / 5MB 以下。
import { useRef, useState } from "react";
import { uploadImage, uploadsUrl } from "../lib/uploader";

interface Props {
  value: string | null; // 現在の fileKey (null = 未設定)
  onChange: (fileKey: string | null) => void;
  label?: string;
}

export function ThumbnailUploader({ value, onChange, label = "サムネイル" }: Props) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const previewUrl = uploadsUrl(value);

  const onFile = async (file: File) => {
    setBusy(true);
    setErr(null);
    setSuccess(false);
    try {
      const r = await uploadImage(file);
      onChange(r.fileKey);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      setErr((e as Error).message ?? "アップロードに失敗しました");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <label className="label">{label}</label>
      {previewUrl && (
        <div className="mb-2">
          <img
            src={previewUrl}
            alt="thumbnail preview"
            className="rounded border border-black/10 max-h-40 object-contain bg-white"
          />
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="text-sm"
          disabled={busy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onFile(f);
          }}
        />
        {value && (
          <button
            type="button"
            className="text-xs text-red-600 underline"
            onClick={() => onChange(null)}
            disabled={busy}
          >
            削除
          </button>
        )}
      </div>
      {busy && (
        <p className="text-xs text-tora-ink/60 mt-1">アップロード中… (自動リサイズ処理中)</p>
      )}
      {err && (
        <p className="text-xs text-red-600 mt-1 p-2 bg-red-50 rounded">
          ❌ {err}
        </p>
      )}
      {success && (
        <p className="text-xs text-green-700 mt-1 p-2 bg-green-50 rounded">
          ✅ アップロード成功 (「更新」ボタンで保存してください)
        </p>
      )}
      <p className="text-xs text-tora-ink/50 mt-1">
        PNG / JPEG / WebP / GIF (大きな画像は自動リサイズされます)
      </p>
    </div>
  );
}
